'use client';

import {
  useEffect,
  useState,
  useCallback,
  createContext,
  useMemo,
  useContext,
} from 'react';
import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from 'next/navigation';
import { getInitialsFromName } from '@/_utils/initials';
import {
  getCurrentUser,
  verifyPasswordForFirebaseToken,
  verifyPasswordWithWorkOS,
  sendAuthLink,
  send2FALink,
  workosCallback,
  getPreferredSignInMethod,
  resetSession,
} from '@/_api/auth';
import type { FirmUserAsAdvisor, User } from '@/_types/models/user';
import { ClientMenu } from './components/ClientMenu';
import { CompanySelector } from './components/CompanySelector';
import type { Company } from '@/_types/models/company';
import type { UserProviderProps, WorkOSAuthData } from './UserProvider.types';
import { Dialog } from '@/_components';
import { LoginContext } from '@/_contexts/LoginProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import {
  getAssignedCompanies,
  setDefaultCompany,
} from '@/_api/client-companies';
import { getFullName } from '@/_utils/user';
import { PUBLIC_PATHNAMES } from '@/_constants/routing';
import { signedOutWithinLastNSeconds } from '@/_utils/auth';
import { DocumentsContext } from '../DocumentsProvider';
import {
  getLocalStorageItem,
  getSessionStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
  setSessionStorageItem,
} from '@/_utils/localstorage';
import { TemplateSignUpInContext } from '@/_contexts/TemplateSignUpInProvider';
import { ClientInactiveCompanyOverlay } from './components/ClientInactiveCompanyOverlay';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { SORABAN_AUTH_TOKEN } from '@/_constants/cookies';
import { useTypingActivityDetection } from './UserProvider.hooks';
import { isSorabanAdmin as getIsSorabanAdmin } from '@/_utils/admin';
import { clearChatThreadsFromCache } from '@/_utils/ai';
import { isSameOriginUrl } from '@/_utils/validations';

let storageEventListenerAdded = false;

const UserContext = createContext<UserProviderProps>({
  advisor: undefined,
  companies: [],
  company: undefined,
  fullName: '',
  isAdvisor: false,
  isBasic: false,
  isCompanyReadOnly: false,
  isFull: false,
  isLoading: false,
  isLoadingUser: false,
  isLoggedIn: false,
  isPartner: false,
  isSorabanAdmin: false,
  isUserReadOnly: false,
  isWaitingToFindOutIfPasswordIsRequired: true,
  loadCurrentUser: (_token: any) => null,
  permissions: [],
  redirect: undefined,
  reloadCompanies: (_token: any) => null,
  resetSession: () => Promise.resolve(),
  selectedClientCompany: undefined,
  selectedCompanyHash: undefined,
  sendMagicLink: (_email) => null,
  setCompanies: () => null,
  setRedirect: () => null,
  setUser: () => null,
  signInPassword: (_username: string, _password: string) => null,
  signInWithWorkOS: (_workosAuthData: WorkOSAuthData) => null,
  signOut: () => null,
  toggleClientMenu: () => null,
  toggleCompanySelector: () => null,
  updateClientCompany: (_company?: Company) => null,
  user: undefined,
});

const UserProvider = (props: any) => {
  const [companies, setCompanies] = useState<Array<Company>>([]);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [user, setUser] = useState<User | undefined>(undefined);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isLoadingUser, setIsLoadingUser] = useState<boolean>(true);
  const [advisor, setAdvisor] = useState<FirmUserAsAdvisor | undefined>(
    undefined,
  );
  const [showCompanySelector, setShowCompanySelector] =
    useState<boolean>(false);
  const [showClientMenu, setShowClientMenu] = useState<boolean>(false);
  const [
    isWaitingToFindOutIfPasswordIsRequired,
    setIsWaitingToFindOutIfPasswordIsRequired,
  ] = useState<boolean>(true);
  const [isClientCompaniesLoading, setClientCompaniesLoading] =
    useState<boolean>(true);
  const [isSigningOut, setIsSigningOut] = useState<boolean>(false);
  const [hasProcessedWorkOSCallback, setHasProcessedWorkOSCallback] =
    useState<boolean>(false);

  const { setLoginState, setTwoFactorMessage, loginState, setUid } =
    useContext(LoginContext);
  const { addToast } = useContext(ToastContext);
  const { reloadDocuments } = useContext(DocumentsContext);
  const { templateRedirect } = useContext(TemplateSignUpInContext);
  const { preferences } = useContext(FirmSettingsContext);

  const { push } = useRouter();

  const { companyHash } = useParams();
  const currentPath = usePathname();
  const searchParams = useSearchParams();
  const redirect: string | null = searchParams.get('redirect');
  const code: string | null = searchParams.get('code');
  const state: string | null = searchParams.get('state');
  const error: string | null = searchParams.get('error');
  const errorDescription: string | null = searchParams.get('error_description');

  // Skip WorkOS callback processing for Stripe OAuth
  const isStripeCallback = Boolean(code && state);

  const setRedirect = useCallback(
    (value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set('redirect', value);

      return params.toString();
    },
    [searchParams],
  );

  // Local constants for user
  const isLoggedIn: boolean = useMemo(
    () => !!user?.id && !isLoading && !isLoadingUser,
    [user?.id, isLoading, isLoadingUser],
  );

  const isAdvisor: boolean = useMemo(
    () => isLoggedIn && !!user?.id && !!user?.is_advisor && !isLoadingUser,
    [user?.id, user?.is_advisor, isLoggedIn, isLoadingUser],
  );

  const isUserReadOnly: boolean = useMemo(
    () => isLoggedIn && !!user?.readonly_access,
    [user, isLoggedIn],
  );

  const isPartner: boolean = useMemo(
    () => isLoggedIn && isAdvisor && user?.firm_access === 'partner',
    [isLoggedIn, isAdvisor, user?.firm_access],
  );

  const isFull: boolean = useMemo(
    () => isLoggedIn && isAdvisor && user?.firm_access === 'full',
    [isAdvisor, isLoggedIn, user?.firm_access],
  );

  const isBasic: boolean = useMemo(
    () => isLoggedIn && isAdvisor && user?.firm_access === 'basic',
    [isAdvisor, isLoggedIn, user?.firm_access],
  );

  const isSorabanAdmin: boolean = useMemo(
    () => getIsSorabanAdmin(user?.email, isAdvisor),
    [user?.email, isAdvisor],
  );

  const selectedCompanyHash: string | null | undefined = useMemo(() => {
    const company: Company | undefined = companies?.find(
      (c) => c.hash_id === companyHash,
    );

    return company?.hash_id || user?.default_company?.hash_id;
  }, [companies, companyHash, user?.default_company?.hash_id]);

  const selectedCompanyId: number | null | undefined = useMemo(() => {
    const company: Company | undefined = companies?.find(
      (c) => c.hash_id === companyHash,
    );

    if (company?.id) return company?.id;
    if (!user?.is_advisor && user?.default_company_id) {
      return user?.default_company_id;
    }

    const companyIdParam: number = parseInt(
      searchParams.get('companyId') as string,
    );
    // This extra check here is because if `companyId` in the params and the user messes with the param itself, the wrong id could end up being passed. So if the `companyId` is coming from the params, we want to ensure it is in that user's assigned companies.
    // Also, we will deprecate `companies` usage in the future and when we do, we'll need to validate `companyIdParam` a different way.
    const companyIsFound: boolean =
      !!companyIdParam && !!companies?.find((c) => c.id === companyIdParam);

    if (companyIsFound) return companyIdParam;

    return null;
  }, [
    searchParams,
    companies,
    companyHash,
    user?.default_company_id,
    user?.is_advisor,
  ]);

  const selectedClientCompany: Company | undefined = useMemo(
    () => companies?.find((company) => company?.id === selectedCompanyId),
    [companies, selectedCompanyId],
  );

  const fullName: string = useMemo(() => getFullName(user), [user]);
  const initials = useMemo(() => getInitialsFromName(fullName), [fullName]);

  const updateClientCompany = async (company?: Company): Promise<void> => {
    if (company?.inactive) return;
    if (!company?.id) return;

    try {
      await setDefaultCompany({
        companyId: company?.id,
        token: token as string,
      });
      const newUser: User = {
        ...user!,
        default_company: company,
        default_company_id: company?.id,
      };

      setUser(newUser);
      push('/');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      reloadDocuments();
    }
  };

  const company: Company | undefined = useMemo(() => {
    if (!user?.default_company_id || !companies?.length) return undefined;

    const selectedCompany = companies?.find(
      (c) => c.id === user?.default_company_id,
    );

    return selectedCompany;
  }, [user?.default_company_id, companies]);

  const isCompanyReadOnly: boolean = useMemo(
    () => isLoggedIn && !!company?.readonly_access,
    [company, isLoggedIn],
  );

  const hasNoActiveCompany: boolean = useMemo(
    () =>
      !isSigningOut &&
      !isAdvisor &&
      !isLoading &&
      !isClientCompaniesLoading &&
      companies?.length > 0 &&
      companies.every((c) => c.inactive),
    [companies, isSigningOut, isAdvisor, isLoading, isClientCompaniesLoading],
  );

  // Loads and sets the current User with an token for Authenticate header
  const loadCurrentUser = useCallback(
    async (token?: string | null, isLoginAttempt?: boolean) => {
      // Specifically set the loading state for the user.
      // If we don't, then we have to use isLoading in other parts
      // of the app and that could be affected by loading in companies.
      // That caused some weird loading conditions around authentication.
      // - Jordan
      setIsLoadingUser(true);

      if (!token) {
        setIsLoadingUser(false);
        return setLoading(false);
      }

      try {
        const user = await getCurrentUser({ token });
        const advisor: FirmUserAsAdvisor = {
          id: user.assigned_advisor_id!,
          avatar: user.assigned_advisor_avatar,
          name: user.assigned_advisor_name,
        };

        const is2FAEnabled: boolean =
          !!user?.enabled_2fa ||
          (isAdvisor && !!preferences?.force_2fa_on_firm_users) ||
          (!isAdvisor && !!preferences?.force_2fa_on_users);
        // Only prompt for 2FA if the user has a phone number on file.
        const hasPhoneNumber: boolean = !!user?.phone_number;
        const shouldPrompt2FA: boolean = Boolean(
          isLoginAttempt && is2FAEnabled && hasPhoneNumber,
        );

        if (shouldPrompt2FA) {
          setLoginState('2fa');

          try {
            await send2FALink({ uid: user?.uid! });
          } catch (error) {
            addToast(
              'error',
              'Sorry, there was a problem sending a two-factor authentication link. Please try again later.',
              { error },
            );
          }
          (globalThis as any).IS_LOGIN_ATTEMPT = false;

          return setTimeout(() => {
            setLoading(false);
            setIsLoadingUser(false);
          }, 120);
        }

        setUser(user);
        setAdvisor(advisor);
      } catch (error) {
        addToast('error', null, { error });
      } finally {
        setIsLoadingUser(false);
        setLoading(false);
      }
    },
    [addToast, isAdvisor, preferences, setLoginState],
  );

  const filteredClientCompanies: Array<Company> = useMemo(
    () => (isAdvisor ? companies : companies?.filter((c) => !c.inactive) || []),
    [companies, isAdvisor],
  );

  // Loads companies
  const loadClientCompanies = useCallback(async () => {
    if (!token || !isLoggedIn) return;
    if (isAdvisor) return setClientCompaniesLoading(false);

    try {
      const companies = await getAssignedCompanies({ token });

      setCompanies(companies);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setClientCompaniesLoading(false);
    }
  }, [addToast, token, isAdvisor, isLoggedIn]);

  const onToggleCompanySelector = () => {
    setShowCompanySelector(!showCompanySelector);
    window.SORABAN_RECENT_LOGIN = false;
  };

  const storedToken = getLocalStorageItem(SORABAN_AUTH_TOKEN);

  // handle login
  useEffect(() => {
    const authStateChanged = async () => {
      // Skip if WorkOS token is present to avoid conflicts
      if (storedToken && storedToken.startsWith('Bearer ')) {
        return;
      }

      await loadCurrentUser(storedToken, (globalThis as any).IS_LOGIN_ATTEMPT);
      setToken(storedToken as string);
    };

    authStateChanged();
  }, [storedToken, loadCurrentUser]);

  // Restore WorkOS authentication state on page load
  useEffect(() => {
    const restoreWorkOSAuth = async () => {
      const storedToken = getLocalStorageItem(SORABAN_AUTH_TOKEN);

      if (storedToken && !token && storedToken.startsWith('Bearer ')) {
        try {
          setToken(storedToken);
          await loadCurrentUser(storedToken, false);
        } catch (error) {
          console.error('Failed to restore WorkOS authentication:', error);
          removeLocalStorageItem(SORABAN_AUTH_TOKEN);
        }
      }
    };

    restoreWorkOSAuth();
  }, [loadCurrentUser, token]);

  // handle incoming redirects
  useEffect(
    () => {
      if (
        redirect &&
        token &&
        loginState !== '2fa' &&
        user &&
        !user.set_password_required
      ) {
        const isSetSignIn: boolean = currentPath.startsWith(
          '/set-sign-in-method',
        );
        const isPasswordReset: boolean =
          currentPath.startsWith('/reset-password');

        setTimeout(() => {
          if (isSetSignIn) return;
          if (isPasswordReset) return;

          const decodedRedirect: string = decodeURIComponent(redirect);

          if (isSameOriginUrl(decodedRedirect)) {
            const finalRedirect: string = decodedRedirect.includes('?')
              ? decodedRedirect
              : `${decodedRedirect}?${searchParams.toString()}`;
            push(finalRedirect);
          } else {
            console.warn('Blocked external redirect attempt:', decodedRedirect);
            push('/');
          }
          setRedirect('');
        }, 1000);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      redirect,
      token,
      push,
      templateRedirect,
      searchParams,
      setRedirect,
      loginState,
      user,
    ],
  );

  useEffect(() => {
    if (token) {
      loadClientCompanies();
    }
  }, [token, loadClientCompanies]);

  useEffect(() => {
    if (isLoggedIn) {
      removeLocalStorageItem('signOut');
    }

    return () => {
      removeLocalStorageItem('signOut');
    };
  }, [isLoggedIn]);

  // keep unauthorized url in redirect param
  useEffect(() => {
    if (isLoading || isLoadingUser || isLoggedIn) {
      return;
    }

    const currentWindowPath = window.location.pathname;
    const currentWindowSearch = window.location.search;
    const currentWindowSearchParams = new URLSearchParams(currentWindowSearch);

    const isCurrentWindowPathPublic = PUBLIC_PATHNAMES.some((p) =>
      currentWindowPath.startsWith(p),
    );

    if (
      isCurrentWindowPathPublic ||
      currentWindowSearchParams.has('redirect')
    ) {
      return;
    }

    if (currentWindowPath === '/') {
      return push('/');
    }

    const redirectUrlValue = `${currentWindowPath}${currentWindowSearch}`;

    return push(`/?redirect=${encodeURIComponent(redirectUrlValue)}`);
  }, [isLoading, isLoadingUser, isLoggedIn, push]);

  // Unified sign in with username and password; if WorkOS is enabled, it will use WorkOS; otherwise, it will use Firebase
  const signInPassword = async (username: string, password: string = '') => {
    try {
      // First, check if WorkOS is enabled for this user
      const preferredSignInMethod = await getPreferredSignInMethod({
        username: username.trim(),
      });

      // Try WorkOS authentication if enabled
      if (preferredSignInMethod?.workos_enabled) {
        try {
          const workosResponse = await verifyPasswordWithWorkOS({
            username: username.trim(),
            password,
          });

          if (workosResponse?.success) {
            if (workosResponse?.requires_2fa) {
              try {
                await send2FALink({ uid: workosResponse.uid! });

                setLoginState('2fa');
                setUid(workosResponse.uid);

                if (workosResponse?.message) {
                  setTwoFactorMessage(workosResponse?.message);
                }

                (globalThis as any).IS_LOGIN_ATTEMPT = false;
                return;
              } catch (error) {}
            } else if (workosResponse?.auth_token && workosResponse?.user) {
              await signInWithWorkOS({
                auth_token: workosResponse.auth_token,
                user: workosResponse.user,
              });
              return;
            }
          }

          // Fall back to Firebase if WorkOS fails
        } catch (workosError: any) {
          // Fall back to Firebase authentication
        }
      }

      // Firebase authentication fallback
      const response = await verifyPasswordForFirebaseToken({
        username: username.trim(),
        password,
      });

      if (response?.error) {
        const toastMessage: string | null =
          response.error === 'Invalid password'
            ? 'The email address or password is incorrect'
            : null;

        return addToast('error', toastMessage, { error: response.error });
      }

      if (response?.firebase_token) {
        (globalThis as any).IS_LOGIN_ATTEMPT = true;
      }
    } catch (error: any) {
      // Handle rate limiting
      if (error?.status === 429) {
        return addToast(
          'error',
          error?.message || 'Too many attempts. Please try again later.',
        );
      }

      console.error(error);
      addToast('error', 'Sorry, there was a problem signing you in');
    }
  };

  // WorkOS authentication
  const signInWithWorkOS = useCallback(
    async (workosAuthData: WorkOSAuthData) => {
      try {
        const bearerToken = `Bearer ${workosAuthData.auth_token}`;

        setLocalStorageItem(SORABAN_AUTH_TOKEN, bearerToken);
        setToken(bearerToken);

        const isLoginAttempt = (globalThis as any).IS_LOGIN_ATTEMPT;
        await loadCurrentUser(bearerToken, isLoginAttempt);

        // If this came from a login attempt, avoid overriding state here.
        // loadCurrentUser handles 2FA prompts and state transitions.
        if (isLoginAttempt) {
          return;
        }

        // Ensure we exit the 2FA screen after successful sign-in
        if (loginState === '2fa') {
          setLoginState('login');
        } else {
          if (code) {
            setLoginState('set-sign-in-method');
          } else {
            setLoginState('login');
          }
        }
      } catch (error) {
        console.error('WorkOS sign in error:', error);
        addToast(
          'error',
          'Sorry, there was a problem signing you in with WorkOS',
          {
            error,
          },
        );
      }
    },
    [addToast, setLoginState, loadCurrentUser, code, loginState],
  );

  // Send a magic link
  const sendMagicLink = async (
    accountantId: number,
    redirect: any,
    uid: string,
  ) => {
    try {
      const { error } = await sendAuthLink({ accountantId, redirect, uid });
      if (!!error) throw new Error(error);
    } catch (error) {
      addToast(
        'error',
        'Sorry, there was a problem sending the sign-in link. Please try again later.',
        {
          error,
        },
      );
    }
  };

  const toggleClientMenu = () => setShowClientMenu(!showClientMenu);

  const signOut = async (shouldSkipLocalStorageSetting = false) => {
    setIsSigningOut(true);

    try {
      // Clear chat history from localStorage
      if (user?.id) {
        clearChatThreadsFromCache(user.id);
      }

      // So we don't end up in a loop
      if (!shouldSkipLocalStorageSetting) {
        if (!signedOutWithinLastNSeconds(10)) {
          setLocalStorageItem('signOut', Date.now().toString()); // triggers refresh in all tabs
        }
      }

      removeLocalStorageItem(SORABAN_AUTH_TOKEN);

      // reset local state
      setUser(undefined);
      setToken(undefined);
      setLoginState('login');

      // reset meta
      setCompanies([]);

      // prevent redirects from picking up logged out session
      setRedirect('');
      window.SORABAN_RECENT_LOGIN = true;

      // reset url
      push('/');
    } catch (error) {
      console.error(error);
    } finally {
      window.location.href = '/';
    }
  };

  const handleResetSession = useCallback(async () => {
    if (!token) return;

    try {
      await resetSession({ token });
    } catch (error) {
      console.debug('Reset session failed:', error);
    }
  }, [token]);

  useEffect(() => {
    // All this conditional logic is here to keep an alert from appearing if the user manually signs themselves out
    let windowId = getSessionStorageItem('windowId');

    if (!windowId) {
      windowId = `window-${Date.now()}-${Math.random()}`;
      setSessionStorageItem('windowId', windowId);
    }

    const handleStorageChange = (event: any) => {
      if (
        event.key === 'signOut' &&
        !!event.newValue &&
        event.newValue !== 'undefined'
      ) {
        signOut(true);
      }
    };

    if (!storageEventListenerAdded) {
      // Adding this event listener will cause the callback to get called in other tabs. That way we can refresh those tabs if the user had to be signed out in one tab. If not, it can create a confusing UX.
      window.addEventListener('storage', handleStorageChange);
      storageEventListenerAdded = true;
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // handle already logged in users with `set_password_required`
  useEffect(() => {
    if (currentPath?.startsWith('/set-sign-in-method')) return;
    if (!user?.id) return;

    if (!!templateRedirect) return push(templateRedirect!);

    setIsWaitingToFindOutIfPasswordIsRequired(false);

    if (user.set_password_required && !user.is_advisor) {
      const params = new URLSearchParams(searchParams.toString());

      if (!params.get('redirect') && currentPath) {
        const searchString = searchParams.toString();
        const fallback = searchString
          ? `${currentPath}?${searchString}`
          : currentPath;

        if (fallback) {
          params.set('redirect', fallback);
        }
      }

      const destination = params.toString()
        ? `/set-sign-in-method?${params.toString()}`
        : '/set-sign-in-method';

      return push(destination);
    }
  }, [
    user?.id,
    user?.set_password_required,
    user?.is_advisor,
    push,
    templateRedirect,
    currentPath,
    searchParams,
  ]);

  // handle advisors missing preferred_sign_in_method
  useEffect(() => {
    if (currentPath?.startsWith('/set-password')) return;
    if (!user?.id) return;
    if (!user.is_advisor) return;

    // TODO @chris improve in the scope of https://linear.app/soraban/issue/SOR-17896/enforce-mfa-for-all-firm-users
    if (
      !user.preferred_sign_in_method &&
      !user?.enabled_2fa &&
      !preferences?.force_2fa_on_firm_users
    ) {
      return push('/set-password');
    }
  }, [
    user?.id,
    user?.enabled_2fa,
    user?.is_advisor,
    user?.preferred_sign_in_method,
    push,
    preferences?.force_2fa_on_firm_users,
    currentPath,
  ]);

  const handleWorkOSCallback = useCallback(async () => {
    // Check for expired or invalid WorkOS links first
    if (error || errorDescription) {
      const isExpiredToken =
        errorDescription?.includes('expired') || error === 'access_denied';

      if (isExpiredToken) {
        addToast(
          'error',
          'The sign-in link has expired. Please request a new one.',
        );
        setLoginState('login');
        setHasProcessedWorkOSCallback(true);
        return;
      }

      addToast('error', `Authentication error: ${errorDescription || error}`);
      setLoginState('login');
      setHasProcessedWorkOSCallback(true);

      return;
    }

    if (!code) return;

    try {
      const data = await workosCallback({ code });
      const isValid: boolean = Boolean(
        data?.success && data?.auth_token && data?.user,
      );

      if (isValid) {
        await signInWithWorkOS({
          auth_token: data.auth_token,
          user: data.user,
        });
      } else {
        addToast('error', 'Authentication failed. Please try again.', {
          error: data,
        });
        setLoginState('login');
      }
    } catch (error) {
      addToast('error', 'Authentication failed. Please try again.', { error });
      setLoginState('login');
    }

    setHasProcessedWorkOSCallback(true);
  }, [
    code,
    error,
    errorDescription,
    addToast,
    setLoginState,
    signInWithWorkOS,
  ]);

  useEffect(() => {
    if (isStripeCallback) return;

    if (!code && !error && !errorDescription) return;
    if (hasProcessedWorkOSCallback) return; // Prevent re-processing

    handleWorkOSCallback();
  }, [
    isStripeCallback,
    code,
    error,
    errorDescription,
    hasProcessedWorkOSCallback,
    handleWorkOSCallback,
  ]);

  // Auto-reset session when user starts typing
  useTypingActivityDetection(handleResetSession, isLoggedIn);

  return (
    <UserContext
      value={{
        advisor,
        companies: filteredClientCompanies,
        company,
        fullName,
        initials,
        isAdvisor,
        isBasic,
        isCompanyReadOnly,
        isFull,
        isLoading,
        isLoadingUser,
        isLoggedIn,
        isPartner,
        isSorabanAdmin,
        isUserReadOnly,
        isWaitingToFindOutIfPasswordIsRequired,
        loadCurrentUser,
        permissions: user?.permissions || [],
        reloadCompanies: loadClientCompanies,
        resetSession: handleResetSession,
        selectedClientCompany,
        selectedCompanyHash,
        selectedCompanyId,
        sendMagicLink,
        setCompanies,
        setRedirect,
        setUser,
        signInPassword,
        signInWithWorkOS,
        signOut,
        toggleClientMenu,
        toggleCompanySelector: onToggleCompanySelector,
        token,
        updateClientCompany,
        user,
      }}
    >
      {hasNoActiveCompany && <ClientInactiveCompanyOverlay signOut={signOut} />}
      {props.children}
      <Dialog
        isOpen={showCompanySelector}
        onClose={onToggleCompanySelector}
        title="Entities"
      >
        <Dialog.Content>
          <CompanySelector onClose={onToggleCompanySelector} />
        </Dialog.Content>
      </Dialog>
      <ClientMenu isOpen={showClientMenu} onClose={toggleClientMenu} />
    </UserContext>
  );
};

export { UserContext, UserProvider };
